"use client";

import { useState, useId } from "react";
import Image from "@/components/ui/image";
import { 
  LayoutDashboard, Users, Ticket, MessageSquare, Megaphone, Bot, 
  Workflow, Plug, CreditCard, Palette, Settings, HelpCircle, 
  Search, Bell, Sun, Moon, Plus, Circle, Calendar, ChevronRight, 
  TrendingUp, Clock, UserPlus, Home, Cloud, X, Check
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import ayraLogoDark from "@/assets/ayra-logo-dark.png";
import ayraLogoLight from "@/assets/ayra-logo-light.png";
import vapiLogo from "@/assets/vapi-logo.png";
import ultravoxLogo from "@/assets/ultravox-logo.jpeg";
import retellLogo from "@/assets/retell-logo.jpeg";

const DashboardMockup = () => {
  const [showAddAgentModal, setShowAddAgentModal] = useState(false);
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const glowId = useId().replace(/:/g, '');
  const filters = {
    unopaq: `unopaq-${glowId}`,
    unopaq2: `unopaq2-${glowId}`,
    unopaq3: `unopaq3-${glowId}`,
  };

  // Theme colors
  const theme = {
    // Backgrounds
    bg: isDarkMode ? 'bg-[#0d0d0d]' : 'bg-[#fafafa]',
    bgPanel: isDarkMode ? 'bg-[#141414]' : 'bg-white',
    bgCard: isDarkMode ? 'bg-[#1a1a1a]' : 'bg-white',
    bgSearch: isDarkMode ? 'bg-[#1f1f1f]' : 'bg-[#f5f5f5]',
    bgWelcome: isDarkMode ? 'bg-gradient-to-r from-[#1a1a1a] to-[#0d0d0d]' : 'bg-gradient-to-r from-[#f0fdf4] to-[#fafafa]',
    bgHover: isDarkMode ? 'hover:bg-[#2a2a2a]' : 'hover:bg-muted/50',
    bgActive: isDarkMode ? 'bg-primary/20' : 'bg-primary/10',
    // Text
    text: isDarkMode ? 'text-[#e8e8e8]' : 'text-foreground',
    textMuted: isDarkMode ? 'text-[#888888]' : 'text-muted-foreground',
    textMutedSoft: isDarkMode ? 'text-[#666666]' : 'text-muted-foreground/60',
    // Borders
    border: isDarkMode ? 'border-[#2a2a2a]' : 'border-border/30',
    borderSoft: isDarkMode ? 'border-[#222222]' : 'border-border/20',
    borderCard: isDarkMode ? 'border-[#2a2a2a]' : 'border-border/50',
    // Shadows
    shadow: isDarkMode ? 'shadow-[0_8px_32px_rgba(0,0,0,0.5)]' : 'shadow-2xl',
    // Charts/bars
    barBg: isDarkMode ? 'bg-primary/30' : 'bg-primary/20',
    barActive: isDarkMode ? 'bg-primary' : 'bg-primary',
  };
  return (
    <motion.div 
      className="w-full max-w-6xl mx-auto group"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
    >
      {/* SVG Filters for Glow Effect */}
      <svg style={{ position: 'absolute', width: 0, height: 0 }}>
        <filter width="300%" x="-100%" height="300%" y="-100%" id={filters.unopaq}>
          <feColorMatrix values="1 0 0 0 0 0 1 0 0 0 0 0 1 0 0 0 0 0 9 0" />
        </filter>
        <filter width="300%" x="-100%" height="300%" y="-100%" id={filters.unopaq2}>
          <feColorMatrix values="1 0 0 0 0 0 1 0 0 0 0 0 1 0 0 0 0 0 3 0" />
        </filter>
        <filter width="300%" x="-100%" height="300%" y="-100%" id={filters.unopaq3}>
          <feColorMatrix values="1 0 0 0.2 0 0 1 0 0.2 0 0 0 1 0.2 0 0 0 0 2 0" />
        </filter>
      </svg>

        {/* Dashboard Container */}
        <motion.div 
          className={`relative rounded-xl md:rounded-2xl overflow-hidden transition-all duration-300 ease-in-out bg-[#111215]`}
          whileHover={{ 
            scale: 1.01,
            transition: { duration: 0.3 }
          }}
        >
        {/* Browser Chrome */}
        <motion.div 
          className={`flex items-center gap-2 px-3 md:px-4 py-2 md:py-3 ${theme.bgPanel} ${theme.border} border-b transition-colors duration-300`}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <div className="flex gap-1 md:gap-1.5">
            <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-[#ff5f57]" />
            <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-[#febc2e]" />
            <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-[#28c840]" />
          </div>
          <div className="flex-1 ml-2 md:ml-4">
            <div className={`${theme.bgSearch} rounded-lg px-2 md:px-4 py-1 md:py-1.5 text-[10px] md:text-xs ${theme.textMuted} max-w-md mx-auto text-center truncate transition-colors duration-300`}>
              app.ayra.ai/dashboard
            </div>
          </div>
        </motion.div>
        <div className={`flex min-h-[280px] md:min-h-[520px] ${theme.bg} transition-colors duration-300`}>
          {/* Sidebar - Hidden on mobile */}
          <motion.div 
            className={`w-44 ${theme.bgPanel} ${theme.border} border-r flex-col hidden md:flex transition-colors duration-300`}
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            {/* Logo */}
            <motion.div 
              className={`px-3 py-3 flex items-center gap-2 ${theme.borderSoft} border-b transition-colors duration-300`}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Image 
                src={isDarkMode ? ayraLogoLight : ayraLogoDark} 
                alt="Ayra AI" 
                width={120}
                height={20}
                className="h-5 w-auto transition-opacity duration-300" 
              />
              <span className={`font-semibold text-[11px] ${theme.text} transition-colors duration-300`}>Ayra AI</span>
              <div className={`ml-auto ${theme.textMutedSoft} text-[10px]`}>‹</div>
            </motion.div>

            {/* Nav Sections */}
            <motion.div 
              className="flex-1 px-2 py-3 space-y-4 overflow-y-auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              {/* Overview */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <div className={`text-[9px] font-medium ${theme.textMutedSoft} uppercase tracking-wider px-2 mb-1.5 transition-colors duration-300`}>Overview</div>
                <nav className="space-y-0.5">
                  <NavItem icon={LayoutDashboard} label="Dashboard" active isDarkMode={isDarkMode} />
                  <NavItem icon={Users} label="Clients" isDarkMode={isDarkMode} />
                  <NavItem icon={Ticket} label="Tickets" isDarkMode={isDarkMode} />
                  <NavItem icon={MessageSquare} label="Messages" isDarkMode={isDarkMode} />
                </nav>
              </motion.div>

              {/* Operations */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                <div className={`text-[9px] font-medium ${theme.textMutedSoft} uppercase tracking-wider px-2 mb-1.5 transition-colors duration-300`}>Operations</div>
                <nav className="space-y-0.5">
                  <NavItem icon={Megaphone} label="Announcements" isDarkMode={isDarkMode} />
                  <NavItem icon={Bot} label="Agents" isDarkMode={isDarkMode} />
                  <NavItem icon={Workflow} label="Workflows" isDarkMode={isDarkMode} />
                  <NavItem icon={Plug} label="Integrations" isDarkMode={isDarkMode} />
                </nav>
              </motion.div>

              {/* Management */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                <div className={`text-[9px] font-medium ${theme.textMutedSoft} uppercase tracking-wider px-2 mb-1.5 transition-colors duration-300`}>Management</div>
                <nav className="space-y-0.5">
                  <NavItem icon={CreditCard} label="Billing & Usage" isDarkMode={isDarkMode} />
                  <NavItem icon={Palette} label="White-Label Studio" isDarkMode={isDarkMode} />
                </nav>
              </motion.div>

              {/* Settings */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.7 }}
              >
                <div className={`text-[9px] font-medium ${theme.textMutedSoft} uppercase tracking-wider px-2 mb-1.5 transition-colors duration-300`}>Settings</div>
                <nav className="space-y-0.5">
                  <NavItem icon={Settings} label="Settings" isDarkMode={isDarkMode} />
                  <NavItem icon={HelpCircle} label="Support" isDarkMode={isDarkMode} />
                </nav>
              </motion.div>
            </motion.div>

            {/* Bottom Status */}
            <motion.div 
              className={`px-3 py-2 ${theme.borderSoft} border-t transition-colors duration-300`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.8 }}
            >
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-primary" />
                <span className={`text-[10px] ${theme.text} font-medium transition-colors duration-300`}>Demo Agency</span>
                <span className={`text-[9px] ${theme.textMuted} ml-auto transition-colors duration-300`}>Active</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Main Content */}
          <motion.div 
            className={`flex-1 flex flex-col ${theme.bg} transition-colors duration-300`}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
          >
            {/* Top Bar */}
            <motion.div 
              className={`h-10 ${theme.bgPanel} ${theme.border} border-b flex items-center px-3 gap-2 transition-colors duration-300`}
              initial={{ opacity: 0, y: -15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <div className="flex items-center gap-1.5">
                <button className={`w-6 h-6 rounded-md ${theme.bgHover} flex items-center justify-center transition-colors duration-300`}>
                  <Plus className={`w-3 h-3 ${theme.textMuted}`} />
                </button>
                <button className={`w-6 h-6 rounded-md ${theme.bgHover} flex items-center justify-center transition-colors duration-300`}>
                  <Circle className={`w-3 h-3 ${theme.textMuted}`} />
                </button>
                <button className={`w-6 h-6 rounded-md ${theme.bgHover} flex items-center justify-center transition-colors duration-300`}>
                  <Calendar className={`w-3 h-3 ${theme.textMuted}`} />
                </button>
              </div>
              
              {/* Search */}
              <div className="flex-1 flex justify-center">
                <div className={`flex items-center gap-2 ${theme.bgSearch} rounded-lg px-3 py-1.5 w-48 transition-colors duration-300`}>
                  <Search className={`w-3 h-3 ${theme.textMutedSoft}`} />
                  <span className={`text-[10px] ${theme.textMutedSoft}`}>Search...</span>
                  <span className={`text-[9px] ${theme.textMutedSoft} ml-auto ${isDarkMode ? 'bg-[#2a2a2a]' : 'bg-white'} px-1 rounded transition-colors duration-300`}>⌘K</span>
                </div>
              </div>

              {/* Right Actions */}
              <div className="flex items-center gap-1.5">
                <button 
                  onClick={() => setIsDarkMode(!isDarkMode)}
                  className={`w-6 h-6 rounded-md ${theme.bgHover} flex items-center justify-center transition-colors duration-300 cursor-pointer`}
                >
                  {isDarkMode ? (
                    <Moon className="w-3 h-3 text-primary" />
                  ) : (
                    <Sun className={`w-3 h-3 ${theme.textMuted}`} />
                  )}
                </button>
                <button className={`w-6 h-6 rounded-md ${theme.bgHover} flex items-center justify-center transition-colors duration-300`}>
                  <Bell className={`w-3 h-3 ${theme.textMuted}`} />
                </button>
                <span className={`text-[9px] ${isDarkMode ? 'bg-primary/20' : 'bg-primary/10'} text-primary px-1.5 py-0.5 rounded font-medium transition-colors duration-300`}>Beta</span>
                <div className="flex items-center gap-1 ml-1">
                  <div className={`w-5 h-5 rounded-full ${isDarkMode ? 'bg-[#2a2a2a]' : 'bg-muted'} transition-colors duration-300`} />
                  <span className={`text-[10px] ${theme.text} transition-colors duration-300`}>Demo</span>
                </div>
              </div>
            </motion.div>

            {/* Page Content */}
            <div className="flex-1 p-2 md:p-4 overflow-y-auto">
              {/* Header */}
              <motion.div 
                className="flex items-start justify-between mb-2 md:mb-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
              >
                <div>
                  <div className={`hidden md:flex items-center gap-1 text-[9px] ${theme.textMuted} mb-1 transition-colors duration-300`}>
                    <Home className="w-3 h-3" />
                    <ChevronRight className="w-2.5 h-2.5" />
                    <span>Dashboard</span>
                  </div>
                  <motion.h1 
                    className={`text-sm md:text-lg font-semibold ${theme.text} transition-colors duration-300`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.6 }}
                  >
                    Overview
                  </motion.h1>
                  <motion.p 
                    className={`text-[8px] md:text-[10px] ${theme.textMuted} hidden sm:block transition-colors duration-300`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.7 }}
                  >
                    Monitor key metrics and manage your platform
                  </motion.p>
                </div>
                <div className="hidden md:flex items-center gap-2">
                  <button 
                    onClick={() => setShowAddAgentModal(true)}
                    className={`flex items-center gap-1 text-[10px] px-2.5 py-1.5 rounded-lg ${isDarkMode ? 'border-[#2a2a2a] bg-[#1a1a1a] hover:bg-[#2a2a2a]' : 'border-border/50 bg-white hover:bg-muted/30'} border ${theme.text} hover:shadow-sm transition-all cursor-pointer duration-300`}
                  >
                    <UserPlus className="w-3 h-3" />
                    Add Agent
                  </button>
                  <button 
                    onClick={() => setShowAddClientModal(true)}
                    className="flex items-center gap-1 text-[10px] px-2.5 py-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-sm transition-all cursor-pointer"
                  >
                    <UserPlus className="w-3 h-3" />
                    Add Client
                  </button>
                </div>
              </motion.div>

              {/* Welcome Card */}
              <motion.div 
                className={`${theme.bgWelcome} rounded-lg md:rounded-xl p-2 md:p-4 mb-2 md:mb-4 ${isDarkMode ? 'border-[#2a2a2a]' : 'border-primary/10'} border transition-colors duration-300`}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                whileHover={{ 
                  scale: 1.02,
                  transition: { duration: 0.3 }
                }}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <motion.div 
                      className={`text-xs md:text-sm ${theme.text} mb-0.5 transition-colors duration-300`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.7 }}
                    >
                      Welcome, <motion.span 
                        className="text-primary font-medium"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.4, delay: 0.8 }}
                      >Sameer</motion.span> 🌙
                    </motion.div>
                    <motion.div 
                      className={`text-[8px] md:text-[10px] ${theme.textMuted} mb-1 md:mb-2 hidden sm:block transition-colors duration-300`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.9 }}
                    >
                      Ready to make today productive ✨
                    </motion.div>
                    <motion.div 
                      className={`text-xl md:text-3xl font-bold ${theme.text} transition-colors duration-300`}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5, delay: 1.0 }}
                    >
                      06:08 PM
                    </motion.div>
                    <motion.div 
                      className={`text-[7px] md:text-[9px] ${theme.textMuted} uppercase tracking-wide hidden sm:block transition-colors duration-300`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 1.1 }}
                    >
                      Tuesday, December 16, 2025
                    </motion.div>
                  </div>
                  <div className="text-right hidden sm:block">
                    <div className="flex items-center gap-1 text-lg md:text-2xl font-bold text-primary">
                      <Cloud className="w-4 md:w-5 h-4 md:h-5" />
                      -13°C
                    </div>
                    <div className={`text-[8px] md:text-[9px] ${theme.textMuted} transition-colors duration-300`}>Montreal</div>
                    <div className="hidden md:flex items-center gap-1.5 mt-2">
                      <button className={`text-[9px] px-2 py-1 rounded ${isDarkMode ? 'border-[#2a2a2a] bg-[#1a1a1a]' : 'border-border/50 bg-white'} border ${theme.text} transition-colors duration-300`}>This Month</button>
                      <button className={`text-[9px] px-2 py-1 rounded ${isDarkMode ? 'border-[#2a2a2a] bg-[#1a1a1a]' : 'border-border/50 bg-white'} border ${theme.text} transition-colors duration-300`}>Export</button>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Metric Cards */}
              <motion.div 
                className="grid grid-cols-2 md:grid-cols-4 gap-1.5 md:gap-3 mb-2 md:mb-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.7 }}
              >
                <MetricCard label="Total Projects" value="24" change="+3" color="primary" isDarkMode={isDarkMode} />
                <MetricCard label="Completion" value="78%" change="+5%" color="primary" isDarkMode={isDarkMode} />
                <div className="hidden md:block"><MetricCard label="Active Users" value="1,847" change="+12%" color="primary" isDarkMode={isDarkMode} /></div>
                <div className="hidden md:block"><MetricCard label="Avg. Response Time" value="32 min" change="0%" color="muted" isDarkMode={isDarkMode} /></div>
              </motion.div>

              {/* Performance Cards - Hidden on small mobile */}
              <motion.div 
                className="hidden sm:grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3 mb-2 md:mb-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.8 }}
              >
                <motion.div 
                  className={`${theme.bgCard} rounded-lg md:rounded-xl p-2 md:p-3 ${theme.border} border transition-colors duration-300`}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.9 }}
                  whileHover={{ 
                    scale: 1.03,
                    transition: { duration: 0.3 }
                  }}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-6 md:w-7 h-6 md:h-7 rounded-lg ${isDarkMode ? 'bg-primary/20' : 'bg-primary/10'} flex items-center justify-center transition-colors duration-300`}>
                      <TrendingUp className="w-3 md:w-3.5 h-3 md:h-3.5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-[10px] md:text-[11px] font-medium ${theme.text} truncate transition-colors duration-300`}>DentalCo is performing strongly</div>
                      <div className={`text-[8px] md:text-[9px] ${theme.textMuted} truncate transition-colors duration-300`}>142 calls this month <span className="text-primary">(+45%)</span></div>
                    </div>
                    <button className={`hidden md:block text-[9px] px-2 py-1 rounded ${isDarkMode ? 'border-[#2a2a2a] bg-[#1a1a1a]' : 'border-border/50 bg-white'} border ${theme.text} transition-colors duration-300`}>View Client</button>
                  </div>
                </motion.div>
                <motion.div 
                  className={`${theme.bgCard} rounded-lg md:rounded-xl p-2 md:p-3 ${theme.border} border hidden md:block transition-colors duration-300`}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 1.0 }}
                  whileHover={{ 
                    scale: 1.03,
                    transition: { duration: 0.3 }
                  }}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-7 h-7 rounded-lg ${isDarkMode ? 'bg-primary/20' : 'bg-primary/10'} flex items-center justify-center transition-colors duration-300`}>
                      <Users className="w-3.5 h-3.5 text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className={`text-[11px] font-medium ${theme.text} transition-colors duration-300`}>You reached 10K calls this month</div>
                      <div className={`text-[9px] ${theme.textMuted} transition-colors duration-300`}>That's a <span className="font-medium">32% increase</span>. Your clients are scaling rapidly.</div>
                    </div>
                    <button className={`text-[9px] px-2 py-1 rounded ${isDarkMode ? 'border-[#2a2a2a] bg-[#1a1a1a]' : 'border-border/50 bg-white'} border ${theme.text} transition-colors duration-300`}>View Analytics</button>
                  </div>
                </motion.div>
              </motion.div>

              {/* Bottom Section - Simplified on mobile */}
              <motion.div 
                className="hidden md:grid grid-cols-3 gap-3"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 1.1 }}
              >
                {/* Top Performers */}
                <motion.div 
                  className={`col-span-2 ${theme.bgCard} rounded-xl p-3 ${theme.border} border transition-colors duration-300`}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 1.2 }}
                  whileHover={{ 
                    scale: 1.02,
                    transition: { duration: 0.3 }
                  }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-primary" />
                      <div>
                        <div className={`text-[11px] font-semibold ${theme.text} transition-colors duration-300`}>Top Performers</div>
                        <div className={`text-[9px] ${theme.textMuted} transition-colors duration-300`}>Your top clients this month</div>
                      </div>
                    </div>
                    <button className="text-[9px] text-primary">View All →</button>
                  </div>
                  <div className="space-y-2">
                    <PerformerRow rank={1} initials="DE" name="DentalCo" stats="142 calls • 89 bookings" category="Healthcare" change="+12%" isDarkMode={isDarkMode} />
                    <PerformerRow rank={2} initials="TE" name="TechCorp" stats="128 calls • 76 bookings" category="Technology" change="+8%" isDarkMode={isDarkMode} />
                    <PerformerRow rank={3} initials="LA" name="LawFirm Plus" stats="115 calls • 68 bookings" category="Legal" change="+15%" isDarkMode={isDarkMode} />
                  </div>
                </motion.div>

                {/* Quick Actions */}
                <div className={`${theme.bgCard} rounded-xl p-3 ${theme.border} border transition-colors duration-300`}>
                  <div className="flex items-center gap-2 mb-3">
                    <Plus className="w-4 h-4 text-primary" />
                    <div>
                      <div className={`text-[11px] font-semibold ${theme.text} transition-colors duration-300`}>Quick Actions</div>
                      <div className={`text-[9px] ${theme.textMuted} transition-colors duration-300`}>Common tasks at your fingertips</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <QuickAction icon={UserPlus} label="Add Client" shortcut="⌘K" isDarkMode={isDarkMode} />
                    <QuickAction icon={Plus} label="Create Agent" shortcut="⌘A" isDarkMode={isDarkMode} />
                    <QuickAction icon={Workflow} label="Create Workflow" shortcut="⌘W" isDarkMode={isDarkMode} />
                    <QuickAction icon={Megaphone} label="Announcement" shortcut="⌘M" isDarkMode={isDarkMode} />
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </motion.div>

        {/* Add Agent Modal */}
      <AnimatePresence>
        {showAddAgentModal && (
          <motion.div 
            className="fixed inset-0 z-50 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setShowAddAgentModal(false)}
          >
            <div className="absolute inset-0 backdrop-blur-sm" />
            <motion.div 
              className="relative bg-[#fafafa] rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden"
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 bg-white border-b border-border/30">
                <h2 className="text-lg font-semibold text-foreground">Create New Agent</h2>
                <button 
                  onClick={() => setShowAddAgentModal(false)}
                  className="w-8 h-8 rounded-lg hover:bg-muted/50 flex items-center justify-center transition-colors"
                >
                  <X className="w-4 h-4 text-muted-foreground" />
                </button>
              </div>

              {/* Progress Steps */}
              <div className="px-6 py-4 bg-white">
                <div className="flex gap-2">
                  <div className="flex-1 h-1 rounded-full bg-primary" />
                  <div className="flex-1 h-1 rounded-full bg-border/50" />
                  <div className="flex-1 h-1 rounded-full bg-border/50" />
                  <div className="flex-1 h-1 rounded-full bg-border/50" />
                  <div className="flex-1 h-1 rounded-full bg-border/50" />
                </div>
              </div>

              {/* Modal Content */}
              <div className="px-6 py-6 bg-[#fafafa]">
                <h3 className="text-base font-semibold text-foreground mb-1">Choose Platform</h3>
                <p className="text-sm text-muted-foreground mb-6">Select where your agent will operate.</p>

                <div className="grid grid-cols-2 gap-3">
                  {/* Ayra Native */}
                  <div 
                    onClick={() => setSelectedPlatform('ayra')}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all hover:shadow-md ${
                      selectedPlatform === 'ayra' ? 'border-primary bg-primary/5' : 'border-border/50 bg-white hover:border-primary/30'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <Image src={ayraLogoDark} alt="Ayra" width={32} height={32} className="object-contain" />
                      <div className="flex-1">
                        <span className="font-medium text-sm text-foreground block mb-0.5">Ayra Native</span>
                        <p className="text-xs text-muted-foreground">Built-in voice AI platform</p>
                      </div>
                    </div>
                  </div>

                  {/* Vapi */}
                  <div 
                    onClick={() => setSelectedPlatform('vapi')}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all hover:shadow-md ${
                      selectedPlatform === 'vapi' ? 'border-primary bg-primary/5' : 'border-border/50 bg-white hover:border-primary/30'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <Image src={vapiLogo} alt="Vapi" width={32} height={32} className="object-contain rounded-lg" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                          <span className="font-medium text-sm text-foreground">Vapi</span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/20 text-primary font-medium">Connected</span>
                        </div>
                        <p className="text-xs text-muted-foreground">3 account(s) connected</p>
                      </div>
                    </div>
                  </div>

                  {/* Retell */}
                  <div 
                    onClick={() => setSelectedPlatform('retell')}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all hover:shadow-md ${
                      selectedPlatform === 'retell' ? 'border-primary bg-primary/5' : 'border-border/50 bg-white hover:border-primary/30'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <Image src={retellLogo} alt="Retell" width={32} height={32} className="object-contain rounded-lg" />
                      <div className="flex-1">
                        <span className="font-medium text-sm text-foreground block mb-0.5">Retell</span>
                        <p className="text-xs text-muted-foreground">Voice AI infrastructure</p>
                      </div>
                    </div>
                  </div>

                  {/* Ultravox */}
                  <div 
                    onClick={() => setSelectedPlatform('ultravox')}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all hover:shadow-md ${
                      selectedPlatform === 'ultravox' ? 'border-primary bg-primary/5' : 'border-border/50 bg-white hover:border-primary/30'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <Image src={ultravoxLogo} alt="Ultravox" width={32} height={32} className="object-contain rounded-lg" />
                      <div className="flex-1">
                        <span className="font-medium text-sm text-foreground block mb-0.5">Ultravox</span>
                        <p className="text-xs text-muted-foreground">High-performance conversational AI</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 bg-white border-t border-border/30 flex items-center justify-between">
                <button 
                  onClick={() => setShowAddAgentModal(false)}
                  className="px-4 py-2 text-sm font-medium text-foreground rounded-lg border border-border/50 hover:bg-muted/30 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  className="px-5 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                  disabled={!selectedPlatform}
                >
                  Continue
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Client Modal */}
      <AnimatePresence>
        {showAddClientModal && (
          <motion.div 
            className="fixed inset-0 z-50 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setShowAddClientModal(false)}
          >
            <div className="absolute inset-0 backdrop-blur-sm" />
            <motion.div 
              className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden"
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-border/30">
                <h2 className="text-lg font-semibold text-foreground">Add New Client</h2>
                <button 
                  onClick={() => setShowAddClientModal(false)}
                  className="w-8 h-8 rounded-lg hover:bg-muted/50 flex items-center justify-center transition-colors"
                >
                  <X className="w-4 h-4 text-muted-foreground" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="px-6 py-6 space-y-4">
                <div>
                  <label className="text-sm font-medium text-foreground block mb-1.5">Client Name</label>
                  <input 
                    type="text" 
                    placeholder="Enter client name"
                    className="w-full px-3 py-2.5 text-sm rounded-lg border border-border/50 bg-[#fafafa] focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground block mb-1.5">Company</label>
                  <input 
                    type="text" 
                    placeholder="Enter company name"
                    className="w-full px-3 py-2.5 text-sm rounded-lg border border-border/50 bg-[#fafafa] focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-foreground block mb-1.5">Email</label>
                    <input 
                      type="email" 
                      placeholder="client@company.com"
                      className="w-full px-3 py-2.5 text-sm rounded-lg border border-border/50 bg-[#fafafa] focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground block mb-1.5">Phone</label>
                    <input 
                      type="tel" 
                      placeholder="+1 (555) 000-0000"
                      className="w-full px-3 py-2.5 text-sm rounded-lg border border-border/50 bg-[#fafafa] focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground block mb-1.5">Industry</label>
                  <select className="w-full px-3 py-2.5 text-sm rounded-lg border border-border/50 bg-[#fafafa] focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-muted-foreground">
                    <option value="">Select industry</option>
                    <option value="healthcare">Healthcare</option>
                    <option value="technology">Technology</option>
                    <option value="legal">Legal</option>
                    <option value="finance">Finance</option>
                    <option value="retail">Retail</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-border/30 flex items-center justify-end gap-3">
                <button 
                  onClick={() => setShowAddClientModal(false)}
                  className="px-4 py-2 text-sm font-medium text-foreground rounded-lg border border-border/50 hover:bg-muted/30 transition-colors"
                >
                  Cancel
                </button>
                <button className="px-5 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-lg transition-colors">
                  Create Client
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const NavItem = ({ icon: Icon, label, active = false, isDarkMode = false }: { icon: any; label: string; active?: boolean; isDarkMode?: boolean }) => (
  <div className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-[10px] cursor-pointer transition-colors duration-300 ${
    active 
      ? isDarkMode ? "bg-primary/20 text-primary font-medium" : "bg-primary/10 text-primary font-medium"
      : isDarkMode ? "text-[#888888] hover:bg-[#2a2a2a]" : "text-muted-foreground hover:bg-muted/50"
  }`}>
    <Icon className="w-3.5 h-3.5" />
    <span>{label}</span>
  </div>
);

const MetricCard = ({ label, value, change, color, isDarkMode = false }: { label: string; value: string; change: string; color: string; isDarkMode?: boolean }) => (
  <motion.div 
    className={`${isDarkMode ? 'bg-[#1a1a1a] border-[#2a2a2a]' : 'bg-white border-border/30'} rounded-lg md:rounded-xl p-2 md:p-3 border transition-colors duration-300`}
    initial={{ opacity: 0, y: 20, scale: 0.95 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
  >
    <motion.div 
      className={`hidden md:flex w-6 h-6 rounded-lg ${isDarkMode ? 'bg-primary/20' : 'bg-primary/10'} items-center justify-center mb-2 transition-colors duration-300`}
      initial={{ opacity: 0, rotate: -180 }}
      animate={{ opacity: 1, rotate: 0 }}
      transition={{ duration: 0.5, delay: 0.1 }}
    >
      <TrendingUp className="w-3 h-3 text-primary" />
    </motion.div>
    <motion.div 
      className="text-[7px] md:text-[9px] text-primary mb-0.5"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.2 }}
    >
      {label}
    </motion.div>
    <motion.div 
      className={`text-base md:text-xl font-bold ${isDarkMode ? 'text-[#e8e8e8]' : 'text-foreground'} transition-colors duration-300`}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
    >
      {value}
    </motion.div>
    <motion.div 
      className={`text-[7px] md:text-[9px] ${color === 'primary' ? 'text-primary' : isDarkMode ? 'text-[#888888]' : 'text-muted-foreground'} transition-colors duration-300`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.4 }}
    >
      {change}
    </motion.div>
    <div className="hidden md:flex items-end gap-0.5 mt-2 h-4">
      {[30, 45, 35, 50, 40, 60, 55].map((h, i) => (
        <div key={i} className={`flex-1 rounded-sm ${i === 6 ? 'bg-primary' : isDarkMode ? 'bg-primary/30' : 'bg-primary/20'} transition-colors duration-300`} style={{ height: `${h}%` }} />
      ))}
    </div>
  </motion.div>
);

const PerformerRow = ({ rank, initials, name, stats, category, change, isDarkMode = false }: { rank: number; initials: string; name: string; stats: string; category: string; change: string; isDarkMode?: boolean }) => (
  <motion.div 
    className={`flex items-center gap-2 py-1.5 ${isDarkMode ? 'border-[#2a2a2a]' : 'border-border/20'} border-b last:border-0 transition-colors duration-300`}
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
    whileHover={{ x: 5, transition: { duration: 0.2 } }}
  >
    <motion.span 
      className={`text-[9px] ${isDarkMode ? 'text-[#888888]' : 'text-muted-foreground'} w-3 transition-colors duration-300`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      {rank}
    </motion.span>
    <motion.div 
      className={`w-6 h-6 rounded-full ${isDarkMode ? 'bg-[#2a2a2a] text-[#e8e8e8]' : 'bg-muted'} flex items-center justify-center text-[9px] font-medium transition-colors duration-300`}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, delay: 0.1 }}
    >
      {initials}
    </motion.div>
    <div className="flex-1">
      <motion.div 
        className={`text-[10px] font-medium ${isDarkMode ? 'text-[#e8e8e8]' : 'text-foreground'} transition-colors duration-300`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.2 }}
      >
        {name}
      </motion.div>
      <motion.div 
        className={`text-[8px] ${isDarkMode ? 'text-[#888888]' : 'text-muted-foreground'} transition-colors duration-300`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.3 }}
      >
        {stats}
      </motion.div>
      <motion.div 
        className={`text-[8px] ${isDarkMode ? 'text-[#666666]' : 'text-muted-foreground/60'} transition-colors duration-300`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.4 }}
      >
        {category}
      </motion.div>
    </div>
    <motion.div 
      className="text-[9px] text-primary"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, delay: 0.5 }}
    >
      {change}
    </motion.div>
    <div className="flex items-end gap-px h-3 w-8">
      {[40, 55, 45, 60, 50].map((h, i) => (
        <div key={i} className={`flex-1 ${isDarkMode ? 'bg-primary/30' : 'bg-primary/30'} rounded-sm transition-colors duration-300`} style={{ height: `${h}%` }} />
      ))}
    </div>
  </motion.div>
);

const QuickAction = ({ icon: Icon, label, shortcut, isDarkMode = false }: { icon: any; label: string; shortcut: string; isDarkMode?: boolean }) => (
  <motion.div 
    className={`flex flex-col items-center justify-center p-2 rounded-lg ${isDarkMode ? 'border-[#2a2a2a] hover:bg-[#2a2a2a]' : 'border-border/30 hover:bg-muted/30'} border cursor-pointer transition-colors duration-300`}
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
    whileHover={{ scale: 1.1, transition: { duration: 0.2 } }}
  >
    <motion.div
      initial={{ opacity: 0, rotate: -180 }}
      animate={{ opacity: 1, rotate: 0 }}
      transition={{ duration: 0.4, delay: 0.1 }}
    >
      <Icon className={`w-4 h-4 ${isDarkMode ? 'text-[#888888]' : 'text-muted-foreground'} mb-1 transition-colors duration-300`} />
    </motion.div>
    <motion.span 
      className={`text-[9px] ${isDarkMode ? 'text-[#e8e8e8]' : 'text-foreground'} transition-colors duration-300`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.2 }}
    >
      {label}
    </motion.span>
    <motion.span 
      className={`text-[8px] ${isDarkMode ? 'text-[#666666]' : 'text-muted-foreground/60'} transition-colors duration-300`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.3 }}
    >
      {shortcut}
    </motion.span>
  </motion.div>
);

export default DashboardMockup;
